import { useEditThreadMetadata } from "@liveblocks/react";
import { useReactFlow } from "@xyflow/react";
import { useEffect } from "react";

import { CANVAS_NODES_DELETED_EVENT } from "../context/canvasNodeDeleteEvents";
import { nodeBoundsById } from "../utils/nodeBounds";
import { orphanPatchesForDeletedNodes } from "./threadPlacement";
import { useCanvasCommentThreads } from "./useCanvasCommentThreads";

/**
 * Detaches, never deletes: a node delete is a geometry operation and the
 * thread is the only copy of a human conversation.
 *
 * `canvas:nodes-deleted` fires synchronously before the storage delete, so the
 * anchor is still in the store and each pin can freeze at its last absolute
 * position. This patches ThreadMetadata only — a commenter holds no storage
 * write, and the marker follows from the webhook.
 */
export function useCanvasCommentOrphanSweep(): void {
  const { threads } = useCanvasCommentThreads();
  const editThreadMetadata = useEditThreadMetadata();
  const { getNodes } = useReactFlow();

  useEffect(() => {
    const onDeleted = (e: Event) => {
      const { nodeIds } = (e as CustomEvent).detail as { nodeIds?: string[] };
      if (!nodeIds?.length || threads.length === 0) return;
      const patches = orphanPatchesForDeletedNodes(
        threads.map((t) => ({ id: t.id, metadata: t.metadata })),
        new Set(nodeIds),
        nodeBoundsById(getNodes()),
      );
      for (const { threadId, patch } of patches) {
        editThreadMetadata({ threadId, metadata: patch });
      }
    };

    window.addEventListener(CANVAS_NODES_DELETED_EVENT, onDeleted);
    return () =>
      window.removeEventListener(CANVAS_NODES_DELETED_EVENT, onDeleted);
  }, [threads, editThreadMetadata, getNodes]);
}
